import type { QueryClient } from "@tanstack/react-query";
import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { setQueryDataOverFetch } from "../../utils/query/setQueryDataOverFetch";
import { USER_PROFILE_KEY } from "../user/queries";
import type { UserLicensesDetails, UserProfile } from "../user/types/user";
import { LICENSES_KEY } from "./queries";
import { LicensesResponse } from "./types/license.api";

export const monthlyTokenLimit = (licenses: LicensesResponse): number =>
    licenses.active[0]?.detail.monthlyLimit.byToken ?? 0;

export const hasValidLicense = (licenses: LicensesResponse | null): boolean =>
    !!licenses && licenses.active.length > 0;

export class LicenseState {
    private eventBus: EventBus;

    private queryClient: QueryClient;

    constructor(eventBus: EventBus, queryClient: QueryClient) {
        this.eventBus = eventBus;
        this.queryClient = queryClient;
    }

    public get licenses(): LicensesResponse | null {
        return (
            this.queryClient.getQueryData<LicensesResponse>(LICENSES_KEY) ??
            null
        );
    }

    /** Plafond de la licence active et jetons dépensés du profil. */
    public get details(): UserLicensesDetails | null {
        const licenses = this.licenses;
        if (!licenses) return null;

        return {
            monthlySpentTokenCount:
                this.queryClient.getQueryData<UserProfile>(USER_PROFILE_KEY)
                    ?.monthlySpentTokenCount ?? 0,
            monthlyTokenCount: monthlyTokenLimit(licenses),
            active: licenses.active,
            expired: licenses.expired,
            upcoming: licenses.upcoming,
        };
    }

    public set(licenses: LicensesResponse): void {
        setQueryDataOverFetch(this.queryClient, LICENSES_KEY, licenses);
    }

    /** Appelé par UserProfileLoader._bootstrapUserProfile() dès réception de la réponse bootstrap. */
    public seedFromBootstrap(licenses: LicensesResponse): void {
        logger.info(LOGS.LicenseManager, "seedFromBootstrap()");

        this.set(licenses);
        this.eventBus.emit("user-license-updated", this.hasValidLicense());
    }

    public hasValidLicense = (): boolean => {
        const isValid = hasValidLicense(this.licenses);
        logger.info(
            LOGS.LicenseManager,
            isValid
                ? "Active license found for current user"
                : "User has no active license",
        );
        return isValid;
    };
}
